'use client';

import { ChevronDown, X } from 'phosphor-icons';
import { useEffect, useState } from 'react';
import { c } from '../../../utils/tailwind-merge';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '../../molecules/DropDownMenu';
import { FacetFilterProps } from './FacetFilter.types';

export const FacetFilter = ({
  facets,
  microcopy,
  onFiltersChange,
  hideSelectedBadges = false,
  initialFilters,
  className = '',
  hideFiltersLabel = false,
  isLoading = true,
}: FacetFilterProps) => {
  const [selectedFilters, setSelectedFilters] = useState<
    Record<string, string | string[]>
  >({});

  // Initialize from external state (e.g., URL params) once
  useEffect(() => {
    if (initialFilters && Object.keys(initialFilters).length > 0) {
      setSelectedFilters((prev) =>
        Object.keys(prev).length === 0 ? initialFilters : prev
      );
    }
  }, [initialFilters]);

  useEffect(() => {
    onFiltersChange(selectedFilters);
  }, [onFiltersChange, selectedFilters]);

  const handleSelectOption = (
    facetId: string,
    value: string,
    isMultiple: boolean
  ) => {
    setSelectedFilters((prev) => {
      const newFilters = { ...prev };

      if (isMultiple) {
        const currentValues = (prev[facetId] as string[]) || [];
        const newValues = currentValues.includes(value)
          ? currentValues.filter((v) => v !== value)
          : [...currentValues, value];
        if (newValues.length > 0) {
          newFilters[facetId] = newValues;
        } else {
          delete newFilters[facetId];
        }
      } else {
        if (newFilters[facetId] === value) {
          delete newFilters[facetId];
        } else {
          newFilters[facetId] = value;
        }
      }

      return newFilters;
    });
  };

  const handleRemoveBadge = (facetId: string, value?: string) => {
    setSelectedFilters((prev) => {
      const newFilters = { ...prev };

      if (value !== undefined) {
        const current = prev[facetId];
        if (Array.isArray(current)) {
          const newValues = current.filter((v) => v !== value);
          if (newValues.length > 0) {
            newFilters[facetId] = newValues;
          } else {
            delete newFilters[facetId];
          }
        } else {
          // Single-selection: removing specific value clears the facet
          if (current === value) {
            delete newFilters[facetId];
          }
        }
      } else {
        // No specific value: clear entire facet
        delete newFilters[facetId];
      }

      return newFilters;
    });
  };

  const handleClearAll = () => {
    setSelectedFilters({});
  };

  const allSelectedItems = Object.entries(selectedFilters).flatMap(
    ([facetId, values]) => {
      const facet = facets.find((f) => f.id === facetId);
      if (!facet) return [];
      if (typeof values === 'string') {
        return [
          {
            facetId,
            value: values,
            facetLabel: facet.label,
            label: facet.options.find((o) => o.value === values)?.label,
          },
        ];
      }
      return values.map((v) => ({
        facetId,
        value: v,
        facetLabel: facet.label,
        label: facet.options.find((o) => o.value === v)?.label,
      }));
    }
  );

  if (isLoading) {
    return null;
  }

  return (
    <div className={c('space-y-4', className)}>
      {!hideFiltersLabel && microcopy.label && (
        <div className="tiny-text mb-2 text-grey-600">{microcopy.label}</div>
      )}
      {/* Facet Dropdowns */}
      <div className="flex flex-wrap gap-2">
        {facets.map((facet) => {
          const isMultiple = facet.type === 'multiple';
          const selectedValues = selectedFilters[facet.id];
          const selectedCount = isMultiple
            ? (selectedValues as string[])?.length || 0
            : selectedValues
              ? 1
              : 0;

          return (
            <DropdownMenu key={facet.id}>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="duration-15 group inline-flex h-10 min-w-24 items-center justify-start gap-2 rounded-lg border border-grey-400 bg-grey-100 px-2.5 outline-offset-2 transition-colors data-[state=open]:bg-white"
                  data-test={`facet-toggle-${facet.id}`}
                >
                  <div className="flex w-full items-center justify-between gap-2">
                    <div className="justify-start text-sm font-medium leading-5 text-grey-600 hover:text-grey-700 group-data-[state=open]:font-medium">
                      {facet.label}{' '}
                      {(facet.displayCount ?? true) &&
                        selectedCount > 0 &&
                        `(${selectedCount})`}
                    </div>
                    <div className="flex items-start justify-start gap-2">
                      <ChevronDown className="h-2.5 w-2.5 text-grey-600 transition-transform group-data-[state=open]:rotate-180" />
                    </div>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="rounded-lg border border-grey-200 bg-white p-2 shadow-sm"
                data-test={`facet-menu-${facet.id}`}
                sideOffset={8}
              >
                {isMultiple ? (
                  <div role="group" aria-label={facet.label}>
                    {facet.options.map((option) => (
                      <DropdownMenuCheckboxItem
                        key={option.value}
                        checked={(
                          (selectedFilters[facet.id] as string[]) || []
                        ).includes(option.value)}
                        // Keep menu open for multi-select; close only on outside click
                        onSelect={(e) => e.preventDefault()}
                        onCheckedChange={() =>
                          handleSelectOption(facet.id, option.value, true)
                        }
                        data-test={`facet-option-${facet.id}-${option.value}`}
                      >
                        {option.label}
                      </DropdownMenuCheckboxItem>
                    ))}
                  </div>
                ) : (
                  <DropdownMenuRadioGroup
                    aria-label={facet.label}
                    value={(selectedFilters[facet.id] as string) || ''}
                    onValueChange={(value) => {
                      handleSelectOption(facet.id, value, false);
                    }}
                  >
                    {facet.options.map((option) => (
                      <DropdownMenuRadioItem
                        key={option.value}
                        value={option.value}
                        data-test={`facet-option-${facet.id}-${option.value}`}
                      >
                        {option.label}
                      </DropdownMenuRadioItem>
                    ))}
                  </DropdownMenuRadioGroup>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        })}
      </div>

      {/* Selected Badges */}
      {!hideSelectedBadges && allSelectedItems.length > 0 && (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {allSelectedItems.map((item) => (
              <div
                key={`${item.facetId}-${item.value}`}
                className="inline-flex items-center gap-2 rounded border border-grey-200 bg-grey-200 px-2 py-1 text-sm font-medium text-grey-700"
              >
                <span className="truncate">
                  {item.facetLabel}: {item.label}
                </span>
                <button
                  onClick={() => handleRemoveBadge(item.facetId, item.value)}
                  className="focus-visible:ring-blue-DEFAULT ml-1 inline-flex items-center rounded-md p-0.5 text-grey-700 hover:bg-grey-200 focus-visible:outline-none focus-visible:ring-2"
                  aria-label={`${microcopy.removeLabel} ${item.label} ${microcopy.filterNoun}`}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={handleClearAll}
            className="text-muted-foreground hover:text-foreground"
          >
            {microcopy.clearLabel}
          </button>
        </div>
      )}
    </div>
  );
};
